import React, { FC, memo } from 'react'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop/Spacing'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import Link from 'next/link'
import { Param } from '@ucheba/ui/components/Param/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import {
  LightGallery,
  LightGalleryItem,
} from '@ucheba/ui/components/LightGallery/desktop'
import { ExternalLogo } from '@ucheba/ui/components/ExternalLogo/desktop'
import styled from 'styled-components'
import { sm } from '@ucheba/utils/helpers/styles/mixins'
import IconStarFilled from '@ucheba/ui/icons/icon__star-filled.svg'
import { ETypeEnum, IInstitutionsListItemProps } from '../types'
import SC from '../base/InstitutionsListItem.styles'
import { useImageColWidth, useInstitutionHref, useInstitutionsListCore } from '../bll'
import { imageWidth } from '../contants'
import { InstitutionsListRanking } from './InstitutionsListRanking'

const { Block, Photos: PhotosSC, PhotosItem, Raiting } = SC

const Photos = styled(PhotosSC)`
  ${sm.breakpoint.lgAndUp`
    a:nth-child(n + 6) {
      display: none;
    }
  `};

  ${sm.breakpoint.lgAndDown`
    a:nth-child(n + 5) {
      display: none;
    }
  `};
`

/** Вуз */
const InstitutionsListItem: FC<IInstitutionsListItemProps> = (props) => {
  const {
    isDivider = true,
    isLogo,
    id,
    name,
    description,
    logo,
    programSummary,
    rankings,
    mediaList,
    specialityId,
    feedbackStats,
    abbreviation,
    shortName,
    tagId,
    type,
    specialityIds,
    activeProgramType,
  } = props

  const href = useInstitutionHref(id)
  const imageColWidth = useImageColWidth(imageWidth)
  const { programs, programsHref, budget, score, price, media } = useInstitutionsListCore(
    {
      id,
      programSummary,
      mediaList,
      specialityId,
      tagId,
      type,
      specialityIds,
      activeProgramType,
    }
  )

  return (
    <Block isDivider={isDivider}>
      <Grid justifyContent='space-between' nowrap>
        <GridCol shrink='initial' width='auto'>
          <Heading
            h3={shortName ? abbreviation || name : name}
            href={href}
            color='blue'
            spacing='xxsmall'
          />

          <Grid spacing='xxsmall'>
            {!!feedbackStats?.score && (
              <GridCol width='auto'>
                <Raiting>
                  <IconStarFilled />

                  <Text
                    fontSize='h5'
                    lineHeight='h5'
                    fontWeight='semibold'
                    color='orange'
                  >
                    {feedbackStats.score}
                  </Text>
                </Raiting>
              </GridCol>
            )}

            <GridCol width='auto'>
              <Link href={programsHref} passHref>
                <Param
                  entity={programs.entity}
                  clarification={programs.clarification}
                  color='blue'
                />
              </Link>
            </GridCol>

            {(type.value === ETypeEnum.vuz ||
              type.value === ETypeEnum.mba ||
              type.value === ETypeEnum.collage ||
              type.value === ETypeEnum.international) && (
              <GridCol width='auto'>
                <Param entity={budget.entity} clarification={budget.clarification} />
              </GridCol>
            )}

            {score.entity && (
              <GridCol width='auto'>
                <Param entity={score.entity} clarification={score.clarification} />
              </GridCol>
            )}

            {price.entity && (
              <GridCol width='auto'>
                <Param entity={price.entity} clarification={price.clarification} />
              </GridCol>
            )}
          </Grid>

          <Spacing spacing='xsmall' />

          {description && <Text>{description}</Text>}

          {media.length > 0 && (
            <>
              <Spacing spacing='medium' />

              <Photos as={LightGallery}>
                {media.map((item, index) => {
                  return (
                    <PhotosItem
                      as={LightGalleryItem}
                      width='130px'
                      height='80px'
                      key={index}
                      {...item}
                    />
                  )
                })}
              </Photos>
            </>
          )}

          {rankings.length > 0 && (
            <>
              <Spacing spacing='medium' />

              <Heading h4='Вуз в рейтингах' spacing='xsmall' />

              <Grid spacing='medium'>
                {rankings.map(({ ranking, rankFrom }) => (
                  <GridCol width='auto' key={ranking.id}>
                    <InstitutionsListRanking
                      id={ranking.id}
                      logo={ranking.logo}
                      place={rankFrom}
                      tooltip={ranking.description}
                    />
                  </GridCol>
                ))}
              </Grid>
            </>
          )}
        </GridCol>

        {isLogo && (
          <GridCol width={imageColWidth}>
            {logo?.fallbackUrl && (
              <ExternalLogo
                logo={logo}
                href={href}
                alt={name}
                maxHeight='90px'
                maxWidth='64px'
              />
            )}
          </GridCol>
        )}
      </Grid>
    </Block>
  )
}

export { InstitutionsListItem }
export default memo(InstitutionsListItem)
